/**
 * PopOutEvents — the Event(s) cell used by the Sales Order table.
 *
 * For 3+ events the collapsed cell previews the first rows and pops out a
 * scrollable, hover-expanded card (via PopOutList). Alignment of the first
 * expanded row with the overlaid cell is handled by PopOutList.
 */

import { useRef, useState, useEffect } from 'react';
import { LoadingSpinnerIcon } from '../icons/LoadingSpinnerIcon';
import { PopOutList } from '../DataTable/PopOutList';
import { Counter } from '@/components/ui/counter';
import { UNDERLINE_SLIDE_IN_GROUP } from '@/components/ui/link-underline';
import { SALES_ORDER_COLUMN_WIDTHS } from '../DataTable/columnWidths';

export interface PopOutEvent {
  event_id: string;
  date: string;
}

const MAX_EXPANDED_HEIGHT = 350;
const PAGE_SIZE = 10;
const HEADER_HEIGHT = 18; // leading-[18px]
// Initial estimate for the popout's header block (label + Counter + divider +
// spacing) so it opens close to aligned. PopOutList then measures the real
// layout and corrects to 0px, so this value is not load-bearing for alignment.
const HEADER_OFFSET = 34;
const POPOUT_PADDING = 12;
const POPOUT_BORDER = 1;

export function EventRow({ event, onClick }: { event: PopOutEvent; onClick?: () => void }) {
  return (
    <button
      type="button"
      className="group/underline flex items-center gap-1.5 min-w-0"
      title={event.date}
      onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
    >
      <span className="inline-flex items-center justify-center rounded-[5px] border border-off-black px-1.5 py-0.5 font-inter text-xs leading-[1.25] font-normal text-off-black whitespace-nowrap shrink-0">
        {event.event_id}
      </span>
      <span className={`${UNDERLINE_SLIDE_IN_GROUP} font-inter text-sm leading-[1.5] text-off-black whitespace-nowrap overflow-hidden text-ellipsis`}>
        {event.date}
      </span>
    </button>
  );
}

function InfiniteScrollEvents({ events, onEventClick }: { events: PopOutEvent[]; onEventClick?: () => void }) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const hasMore = visibleCount < events.length;
  const visibleEvents = events.slice(0, visibleCount);

  useEffect(() => {
    if (!sentinelRef.current) return;

    let loadTimer: ReturnType<typeof setTimeout>;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadTimer = setTimeout(() => {
            setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, events.length));
          }, 800);
        }
      },
      { root: scrollRef.current, threshold: 0.1 },
    );

    observer.observe(sentinelRef.current);
    return () => {
      clearTimeout(loadTimer);
      observer.disconnect();
    };
  }, [events.length, visibleCount]);

  return (
    // PopOutList aligns this element's top with the cell's first row,
    // so do not add padding/margin above the first EventRow.
    <div
      ref={scrollRef}
      className="relative [&::-webkit-scrollbar]:hidden"
        style={{
          overflowX: 'hidden',
          overflowY: 'auto',
          ...(events.length > PAGE_SIZE && { maxHeight: MAX_EXPANDED_HEIGHT - HEADER_HEIGHT - (POPOUT_PADDING + POPOUT_BORDER) * 2 }),
          scrollbarWidth: 'none',
        }}
      >
        <div className="flex flex-col gap-1.5">
          {visibleEvents.map((event, i) => (
            <EventRow key={i} event={event} onClick={onEventClick} />
          ))}
          {hasMore && (
            <div ref={sentinelRef} className="flex items-center justify-center py-2">
              <LoadingSpinnerIcon className="size-4 text-grey" />
            </div>
          )}
        </div>
      </div>
  );
}

export function PopOutEvents({ events, defaultOpen = false, width, onEventClick }: { events: PopOutEvent[]; defaultOpen?: boolean; width?: number; onEventClick?: () => void }) {
  const popoverWidth = width ?? SALES_ORDER_COLUMN_WIDTHS.events;

  return (
    <PopOutList
      width={popoverWidth}
      defaultOpen={defaultOpen}
      headerOffset={HEADER_OFFSET}
      collapsed={
        <div className="flex flex-col gap-1.5">
          {events.slice(0, 2).map((event, i) => (
            <EventRow key={i} event={event} onClick={onEventClick} />
          ))}
        </div>
      }
      header={
        <div className="flex items-center gap-1.5">
          <span className="font-inter text-sm font-semibold leading-[1.5] text-grey">
            {events.length === 1 ? 'Event' : 'Events'}
          </span>
          <Counter variant="soft" value={events.length} />
        </div>
      }
      expanded={
        <InfiniteScrollEvents events={events} onEventClick={onEventClick} />
      }
    />
  );
}
